import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ApiDialogActionType } from '@/core/generated/api';
import { dialogService } from '@/entities/UserDialog/api/dialogService';
import { useUserDialogStore } from '@/entities/UserDialog/model/store';
import { dialogTemplateSchema, type DialogTemplateFormValues } from '@/entities/UserDialog/model/schema';
import { dialogTemplateToForm, dialogTemplateToRequest, dialogActionToOptions } from '@/entities/UserDialog/mappers';
import { DIALOG_ACTION_OPTIONS, DIALOG_ANSWER_OPTIONS } from '@/entities/UserDialog/config/dialog.config';
import {
    EntityForm,
    EntityFormFields,
    EntityFormLayout,
    EntityFormStack,
} from '@/shared/ui/EntityFormLayout/EntityFormLayout';
import { createRHFFields } from '@/shared/ui/RHFFields/RHFFields';
import { BackButton } from '@/shared/ui/BackButton/BackButton';
import buttonClasses from '@/styles/buttons.module.scss';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestErrorBanner } from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import styles from './UserDialogs.module.scss';

const Fields = createRHFFields<DialogTemplateFormValues>('dialog-template');

/** Позволяет настроить содержание и действия диалогового окна */
export const DialogTemplateFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { templates, loadTemplates } = useUserDialogStore();
    const item = templates.data?.find(template => template.id === id);

    const [defaultValues] = useState(() => ({
        ...dialogTemplateToForm(),
        code: `dialog-${crypto.randomUUID()}`,
    }));

    const form = useForm<DialogTemplateFormValues>({
        resolver: zodResolver(dialogTemplateSchema),
        defaultValues
    });

    const { fields, append, remove, replace } = useFieldArray({
        control: form.control,
        name: 'options'
    });

    useEffect(() => { void loadTemplates(); }, [loadTemplates]);
    useEffect(() => { if (item) form.reset(dialogTemplateToForm(item)); }, [item, form]);
    const action = form.watch('action');
    const feedback = action === ApiDialogActionType.CollectFeedback;
    const system = Boolean(item?.systemBroadcastId);
    const ready = !id || Boolean(item);

    /** Сохраняет текущую конфигурацию */
    const save = form.handleSubmit(async values => {
        if (!ready || (id && !form.formState.isDirty)) return;

        try {
            await dialogService.saveTemplate(dialogTemplateToRequest(values, item?.version), id);
            toast.success('Шаблон сохранён');
            navigate('/user-dialogs');
        } catch { /* Ошибка показана общим обработчиком API */ }
    });

    return <EntityFormLayout title={id
        ? 'Конфигурация диалога'
        : 'Новый шаблон диалога'
    }>
        <EntityFormStack>
            <div className={buttonClasses.buttonGroup}>
                <BackButton
                    url="/user-dialogs"
                    title="К списку"
                />
                {item?.systemBroadcastId &&
                    <ActionButton
                        type="button"
                        color="secondary"
                        icon="pi pi-chart-bar"
                        label="Статистика выпуска"
                        onClick={() => navigate(`/user-dialogs/broadcasts/${item.systemBroadcastId}`)}
                    />
                }
            </div>
            <RequestErrorBanner
                {...templates}
                fallbackMessage="Не удалось загрузить шаблон"
                onRetry={loadTemplates}
            />
            {id && templates.status === RequestStatus.Success && !item &&
                <p role="alert">Шаблон не найден</p>
            }
            <p>Изменения применяются к ожидающим диалогам этого шаблона. Ответы пользователей сохраняются.</p>
            <FormProvider {...form}>
                <EntityForm onSubmit={save}>
                    <EntityFormStack>
                        <Fields.TextField
                            name="title"
                            label="Заголовок"
                            maxLength={100}
                        />
                        <Fields.TextareaField
                            name="text"
                            label="Описание"
                            maxLength={300}
                        />
                    </EntityFormStack>
                    <div className={styles.options}>
                        <Fields.SelectField
                            name="action"
                            label="Обработчик ответа"
                            disabled={system}
                            options={system
                                ? DIALOG_ACTION_OPTIONS
                                : DIALOG_ACTION_OPTIONS.filter(option => option.id === ApiDialogActionType.Acknowledge || option.id === ApiDialogActionType.RecordAnswer || option.id === ApiDialogActionType.CollectFeedback)
                            }
                            onValueChange={value => {
                                const selected = DIALOG_ACTION_OPTIONS.find(option => option.id === value)?.id;

                                if (system || selected == null) return;

                                replace(dialogActionToOptions(selected));

                                form.setValue('textInput', selected === ApiDialogActionType.CollectFeedback
                                    ? {
                                        label: 'Ваше мнение',
                                        placeholder: 'Поделитесь впечатлением',
                                        maxLength: 1000,
                                    }
                                    : null,
                                    { shouldDirty: true });
                            }}
                        />
                        <Fields.CheckboxField
                            name="isEnabled"
                            label="Шаблон включён"
                        />
                    </div>
                    {feedback &&
                        <EntityFormStack>
                            <h2>Поле обратной связи</h2>
                            <EntityFormFields>
                                <Fields.TextField
                                    name="textInput.label"
                                    label="Подпись поля ввода"
                                    maxLength={100}
                                    isRequired
                                />
                                <Fields.NumberField
                                    name="textInput.maxLength"
                                    label="Максимальная длина ответа"
                                    min={1}
                                    max={4000}
                                    maxFractionDigits={0}
                                    isRequired
                                />
                            </EntityFormFields>
                            <Fields.TextareaField
                                name="textInput.placeholder"
                                label="Подсказка в поле"
                                maxLength={300}
                            />
                            <p>Для ответа нужен текст. Кнопка «Пропустить» не отправляет черновик.</p>
                        </EntityFormStack>
                    }
                    <EntityFormFields>
                        <Fields.TextField
                            name="routeName"
                            label="Маршрут показа (необязательно)"
                            placeholder="/orders"
                        />
                        <Fields.TextField
                            name="routeEntityId"
                            label="UUID заказа или чата (необязательно)"
                        />
                    </EntityFormFields>
                    <h2>Кнопки</h2>
                    {fields.map((field, index) => <div
                        key={field.id}
                        className={styles.options}
                    >
                        <Fields.SelectField
                            name={`options.${index}.optionType`}
                            label="Значение ответа"
                            options={DIALOG_ANSWER_OPTIONS}
                            disabled={system || feedback || action === ApiDialogActionType.Acknowledge}
                        />
                        <Fields.TextField
                            name={`options.${index}.text`}
                            label="Подпись кнопки"
                            maxLength={100}
                        />
                        {!system && !feedback && action !== ApiDialogActionType.Acknowledge &&
                            <ActionButton
                                type="button"
                                label="Убрать кнопку"
                                color="secondary"
                                onClick={() => remove(index)}
                            />
                        }
                    </div>)}
                    {form.formState.errors.options?.root?.message &&
                        <p role="alert">{form.formState.errors.options.root.message}</p>
                    }
                    {!system && action === ApiDialogActionType.RecordAnswer &&
                        <ActionButton
                            type="button"
                            label="Добавить кнопку"
                            disabled={fields.length >= 5}
                            onClick={() => {
                                const option = DIALOG_ANSWER_OPTIONS.find(candidate => !form.getValues('options').some(current => current.optionType === candidate.id));

                                if (option) append({
                                    optionType: option.id,
                                    text: ''
                                });
                            }}
                        />
                    }
                    <ActionButton
                        type="submit"
                        permission="admin.dialogs.manage"
                        label="Сохранить"
                        loading={form.formState.isSubmitting}
                        disabled={!ready || Boolean(id && !form.formState.isDirty)}
                    />
                </EntityForm>
            </FormProvider>
        </EntityFormStack>
    </EntityFormLayout>;
};
